import type { Page } from '../../app/feature';
import { esc, q } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { fmt, money } from '../../shared/format';
import { getState } from '../../shared/store/store';
import { eventPrizes, pozoOf, leadById, prizeById } from '../../shared/store/selectors';
import { eligiblePool } from '../../domain/draw-engine';
import { href } from '../../app/router';

/** Sorteios: lista com status e criação. Cada sorteio tem sua página (`/sorteos/:id`). */
export const events: Page = {
  id: 'sorteos',
  pattern: '/sorteos',
  roles: ['ADMIN', 'SUPPORT'],
  nav: { key: 'nav.sorteos', icon: '🎡', group: 'ops' },

  template: () => `
<div class="view-head">
  <div>
    <h2 data-i18n="ev.title">Sorteos</h2>
    <p data-i18n="ev.sub4">Cada transmisión es un sorteo con su día, sus premios y su anfitrión. Al crear uno defines los tres de una vez; dentro de su página ajustas lo que haga falta.</p>
  </div>
  <div class="spacer"></div>
  <a class="btn btn-primary" href="${href('/sorteos/nuevo')}">+ <span data-i18n="ev.new">Nuevo sorteo</span></a>
</div>

<div class="cols">
  <div class="span-12">
    <div class="panel-head"><h3><span data-i18n="ev.pendingTitle">Pendientes</span> <span class="count" id="ev-pending-count"></span></h3><div class="spacer"></div><span class="hint" data-i18n="ev.listHint">Haz clic en un sorteo para configurarlo.</span></div>
    <div class="ev-list" id="ev-list"></div>
    <button class="ev-more" id="ev-more-pending" hidden></button>
  </div>
  <div class="span-12 ev-past-block">
    <div class="panel-head"><h3><span data-i18n="ev.pastTitle">Realizados</span> <span class="count" id="ev-past-count"></span></h3><div class="spacer"></div><span class="hint" data-i18n="ev.pastHint">Número sorteado y ganador de cada premio, con el sello y la semilla en la página del sorteo.</span></div>
    <div class="ev-list ev-list-past" id="ev-list-past"></div>
    <button class="ev-more" id="ev-more-past" hidden></button>
  </div>
</div>`,

  mount() {
    // "Ver todos" / "Ver menos": alterna a lista entre os primeiros itens e a lista inteira com scroll
    for (const key of ['pending', 'past'] as const) {
      q(`#ev-more-${key}`).onclick = () => { expanded[key] = !expanded[key]; events.render({}, {}); };
    }
  },

  render() {
    const s = getState();
    const isPast = (e: typeof s.events[number]) => e.status === 'done' || e.status === 'cancelled';
    // pendentes: o mais próximo primeiro; ao vivo sempre no topo; sem data no fim
    const upcoming = s.events.filter(e => !isPast(e)).sort((a, b) =>
      (a.status === 'live' ? 0 : 1) - (b.status === 'live' ? 0 : 1) || (a.at || '9').localeCompare(b.at || '9'));
    // realizados: o que foi sorteado por último em cima (hora do último resultado; sem resultado, a data do sorteio)
    const lastDrawAt = (id: string, fallback: string): number => {
      const ts = s.draws.filter(d => d.eventId === id).map(d => d.at);
      return ts.length ? Math.max(...ts) : (Date.parse(fallback.replace(' ', 'T')) || 0);
    };
    const past = s.events.filter(isPast).sort((a, b) => lastDrawAt(b.id, b.at || '') - lastDrawAt(a.id, a.at || ''));
    /** Sorteio já realizado: número e ganhador de cada prêmio, direto no cartão. */
    const results = (eventId: string): string => {
      const ds = s.draws.filter(d => d.eventId === eventId);
      if (!ds.length) return '';
      return `<div class="ev-card-results">${ds.map(d => {
        const p = prizeById(d.prizeId), l = leadById(d.leadId);
        return `<span class="evr"><b>${esc(d.number)}</b><span>${esc(l?.name ?? '—')}</span><small>${esc(p ? `${p.emoji} ${p.name}` : '')}</small></span>`;
      }).join('')}</div>`;
    };
    const card = (e: typeof s.events[number]): string => {
      const pz = eventPrizes(e.id), pend = pz.filter(p => p.status === 'pending').length;
      const done = isPast(e);
      const ds = s.draws.filter(d => d.eventId === e.id);
      // realizado: o bolão vivo é zero por definição; o que interessa é o que participou e o que foi entregue
      const pool = done ? (ds[0]?.poolSize ?? 0) : eligiblePool(s, e).length;
      const delivered = ds.reduce((sum, d) => sum + (prizeById(d.prizeId)?.value || 0), 0);
      const statusChip = e.status === 'live' ? 'chip-green' : e.status === 'scheduled' ? 'chip-orange' : e.status === 'done' ? 'chip-done' : 'chip-dim';
      return `<a class="ev-card ${e.status === 'done' || e.status === 'cancelled' ? 'is-past' : ''}" href="${href('/sorteos/' + e.id)}">
        <div class="ev-card-main">
          <div class="inline" style="gap:6px">
            <span class="chip ${e.tier === 'mensual' ? 'chip-gold' : 'chip-orange'}">${esc(e.tier === 'mensual' ? t('tier.monthly') : t('tier.weekly'))}</span>
            <span class="chip ${statusChip}">${esc(t('evst.' + e.status))}</span>
          </div>
          <b class="ev-card-name">${esc(e.name)}</b>
          <div class="ev-card-meta">📅 ${esc(e.at || t('ov.noDate'))} · 🎙 ${e.hostName ? esc(e.hostName) : `<span style="color:var(--gold)">${esc(t('ev.nohost'))}</span>`}</div>
        </div>
        ${results(e.id)}
        <div class="ev-card-stats">
          ${done
            ? `<div><b>${ds.length}<small>/${pz.length}</small></b><span>${esc(t('host.delivered'))}</span></div>
               <div><b>${money(delivered)}</b><span>${esc(t('ev.deliveredPozo'))}</span></div>
               <div><b>${fmt(pool)}</b><span>${esc(t('host.participated'))}</span></div>`
            : `<div><b>${pend}<small>/${pz.length}</small></b><span>${esc(t('host.prizes'))}</span></div>
               <div><b>${money(pozoOf(e.id))}</b><span>${esc(t('stage.pozo'))}</span></div>
               <div><b>${fmt(pool)}</b><span>${esc(t('stage.cartelas'))}</span></div>`}
        </div>
        <span class="btn btn-sm ev-card-go">${esc(t('pl.open'))} →</span>
      </a>`;
    };
    q('#ev-list').innerHTML = upcoming.length ? upcoming.map(card).join('') : `<div class="panel empty">${esc(t('ev.nonePending'))}</div>`;
    fitList('pending', upcoming.length);
    q('#ev-pending-count').textContent = upcoming.length ? String(upcoming.length) : '';
    /** Sorteio realizado: cabeçalho com o resumo e, abaixo, uma linha por prêmio (prêmio · número · ganhador). */
    const pastCard = (e: typeof s.events[number]): string => {
      const pz = eventPrizes(e.id);
      const ds = s.draws.filter(d => d.eventId === e.id);
      const delivered = ds.reduce((sum, d) => sum + (prizeById(d.prizeId)?.value || 0), 0);
      const participated = ds[0]?.poolSize ?? 0;
      const cancelled = e.status === 'cancelled';
      const rows = pz.filter(p => p.status !== 'cancelled').map((p, i) => {
        const d = ds.find(x => x.prizeId === p.id);
        const l = d ? leadById(d.leadId) : undefined;
        return `<div class="evp-row${d ? '' : ' is-empty'}">
          <span class="evp-pos">${i + 1}º</span>
          <span class="evp-prize"><i>${esc(p.emoji || '🎁')}</i><span><b>${esc(p.name)}</b><small>${p.value > 0 ? money(p.value) : esc(t('ev.noValue'))}</small></span></span>
          <span class="evp-num">${d ? d.number.split('').map(ch => `<b>${ch}</b>`).join('') : '—'}</span>
          <span class="evp-win">${d ? `<small>🏆 ${esc(t('st.winner'))}</small><b>${esc(l?.name ?? '—')}</b>` : `<small>${esc(t('st.pending'))}</small>`}</span>
        </div>`;
      }).join('');
      return `<a class="evp${cancelled ? ' is-cancelled' : ''}" href="${href('/sorteos/' + e.id)}">
        <div class="evp-head">
          <div class="evp-title">
            <div class="inline" style="gap:6px">
              <span class="chip ${e.tier === 'mensual' ? 'chip-gold' : 'chip-orange'}">${esc(e.tier === 'mensual' ? t('tier.monthly') : t('tier.weekly'))}</span>
              <span class="chip ${cancelled ? 'chip-dim' : 'chip-done'}">${cancelled ? esc(t('evst.cancelled')) : '✓ ' + esc(t('evst.done'))}</span>
            </div>
            <b class="evp-name">${esc(e.name)}</b>
            <div class="evp-meta">📅 ${esc(e.at || t('ov.noDate'))} · 🎙 ${esc(e.hostName || t('ev.nohost'))}</div>
          </div>
          <div class="evp-sum">
            <div><b>${ds.length}<small>/${pz.length}</small></b><span>${esc(t('host.delivered'))}</span></div>
            <div><b>${money(delivered)}</b><span>${esc(t('ev.deliveredPozo'))}</span></div>
            <div><b>${fmt(participated)}</b><span>${esc(t('host.participated'))}</span></div>
          </div>
        </div>
        <div class="evp-rows">${rows || `<div class="evp-row is-empty"><span class="evp-win"><small>${esc(t('ev.noDraws'))}</small></span></div>`}</div>
        <div class="evp-foot"><span>🔒 ${esc(t('ev.auditHint'))}</span><span class="evp-go">${esc(t('pl.open'))} →</span></div>
      </a>`;
    };
    q('#ev-list-past').innerHTML = past.length ? past.map(pastCard).join('') : `<div class="panel empty">${esc(t('ev.nonePast'))}</div>`;
    fitList('past', past.length);
    q('#ev-past-count').textContent = past.length ? String(past.length) : '';
    q('.ev-past-block').classList.toggle('is-empty', past.length === 0);
  }
};

/** Quantos cartões aparecem antes do "Ver todos" (pendentes pedem mais visão; realizados são histórico). */
const PREVIEW = { pending: 4, past: 3 } as const;
const expanded = { pending: false, past: false };

/**
 * Mostra só os primeiros cartões da lista; "Ver todos" abre a lista inteira dentro de uma área com
 * scroll (a página não cresce sem fim), e "Ver menos" volta ao resumo.
 */
function fitList(key: 'pending' | 'past', total: number): void {
  const list = q(key === 'pending' ? '#ev-list' : '#ev-list-past');
  const btn = q<HTMLButtonElement>(`#ev-more-${key}`);
  const limit = PREVIEW[key];
  const open = expanded[key];
  Array.from(list.children).forEach((el, i) => { (el as HTMLElement).hidden = !open && i >= limit; });
  list.classList.toggle('is-scroll', open && total > limit);
  btn.hidden = total <= limit;
  btn.textContent = open ? t('ev.showLess') : `${t('ev.showAll')} (${total})`;
  if (!open) list.scrollTop = 0;
}
